import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Upload, Cpu, Users, BarChart3, Repeat, Database, Sparkles, Shield, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CtaLink } from "@/components/landing/CtaLink";
import { PublicPageLayout } from "@/components/landing/PublicPageLayout";
import { SEO } from "@/components/SEO";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } }
};

const steps = [
  {
    number: "01",
    title: "Upload your tender",
    description: "Drop in your tender document. Sorvaine extracts every requirement, compliance item, and evaluation criterion automatically — and tells you whether to bid at all.",
    icon: Upload,
  },
  {
    number: "02",
    title: "Get your Go/No-Go verdict",
    description: "Before you write a word, Sorvaine analyses your win history, client track record, and capability alignment. Bid with confidence or protect your team's time.",
    icon: BarChart3,
  },
  {
    number: "03",
    title: "Build your proposal",
    description: "AI generates a tailored outline and drafts every section against your Knowledge Vault and the specific tender requirements. Not generic output — your content, your voice.",
    icon: Cpu,
  },
  {
    number: "04",
    title: "Track your readiness",
    description: "Real-time scoring tells you exactly how ready your proposal is — requirements coverage, content completeness, evaluator alignment. You know where to focus.",
    icon: Users,
  },
  {
    number: "05",
    title: "Submit and get smarter",
    description: "Every win and loss feeds your analytics. Which content wins deals. Which sections score highest. What your competitors are doing. The system gets sharper with every bid.",
    icon: Repeat,
  },
];

const capabilities = [
  {
    icon: Database,
    title: "Knowledge Vault",
    description: "Centralise past responses, content, and institutional knowledge in one searchable system.",
  },
  {
    icon: Sparkles,
    title: "AI Drafting",
    description: "Generate structured, relevant answers — not generic outputs. Grounded in your real content.",
  },
  {
    icon: Users,
    title: "Collaboration",
    description: "Work across teams with clear ownership, review flows, and version control.",
  },
  {
    icon: BarChart3,
    title: "Bid Intelligence",
    description: "Learn from every submission and continuously improve future performance.",
  },
];

const differentiators = [
  "Sorvaine is not a prompt-based tool.",
  "It's a system designed for repeatable, strategic winning.",
  "It learns from every submission.",
  "It builds long-term competitive advantage.",
];

const securityPoints = [
  { icon: Lock, text: "Your data is private and isolated" },
  { icon: Shield, text: "We do not train models on your data" },
  { icon: Shield, text: "Enterprise-ready infrastructure" },
];

export default function Platform() {
  return (
    <PublicPageLayout>
      <SEO
        title="Platform | Sorvaine — How It Works"
        description="See how Sorvaine takes teams from tender to submission with structured workflows, AI generation, and continuous improvement."
        canonical="https://sorvaine.com/platform"
      />

      {/* Hero */}
      <section className="pt-20 pb-12 md:pt-40 md:pb-28 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div variants={fadeIn} className="flex items-center gap-3 mb-10">
              <div className="w-2 h-2 rounded-full bg-primary" />
              <div className="w-10 h-px bg-gradient-to-r from-primary to-transparent" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-medium">Platform</span>
            </motion.div>

            <motion.h1
              variants={fadeIn}
              className="text-[30px] sm:text-5xl md:text-6xl lg:text-[4.5rem] font-bold tracking-[-0.02em] leading-[1.06] mb-6 md:mb-8"
            >
              The complete system for winning more tenders.
            </motion.h1>

            <motion.p
              variants={fadeIn}
              className="text-muted-foreground text-base md:text-xl leading-[1.7] max-w-[52ch] mb-8 md:mb-12"
            >
              From the moment a tender lands to the day you submit — every step handled, tracked, and improving.
            </motion.p>

          </motion.div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-16 md:py-32 border-t border-border/40">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            <motion.div variants={fadeIn} className="max-w-2xl mb-10 md:mb-20">
              <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-medium block mb-4 md:mb-6">Workflow</span>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] mb-4 md:mb-6">
                A system for every proposal
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-[1.7]">
                From first draft to final submission, Sorvaine guides every step.
              </p>
            </motion.div>

            <div className="space-y-6">
              {steps.map((step, i) => (
                <motion.div
                  key={step.number}
                  variants={fadeIn}
                  className="group relative flex flex-col gap-4 md:gap-6 p-6 md:p-8 rounded-2xl landing-card hover:border-primary/20 transition-all duration-300"
                >
                  {/* Number + Icon row */}
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-[11px] tracking-[0.2em] font-medium text-primary">{step.number}</span>
                      {i < steps.length - 1 && (
                        <div className="hidden md:block w-px h-8 bg-gradient-to-b from-border/60 to-transparent" />
                      )}
                    </div>
                    <div className="w-10 h-10 rounded-lg border border-primary/30 bg-primary/10 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                      <step.icon className="w-4 h-4 text-primary transition-colors duration-300" />
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold tracking-tight mb-2 text-foreground/90">{step.title}</h3>
                    <p className="text-muted-foreground text-sm md:text-base leading-[1.7]">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-16 md:py-32 border-t border-border/40">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            <motion.div variants={fadeIn} className="max-w-2xl mb-10 md:mb-20">
              <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-medium block mb-4 md:mb-6">Capabilities</span>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
                Built for how proposal teams actually work
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {capabilities.map((cap) => (
                <motion.div
                  key={cap.title}
                  variants={fadeIn}
                  className="group p-8 md:p-10 rounded-2xl landing-card hover:border-primary/20 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg border border-primary/30 bg-primary/10 flex items-center justify-center mb-6 transition-colors duration-300">
                    <cap.icon className="w-4 h-4 text-primary transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight mb-3 text-foreground/90">{cap.title}</h3>
                  <p className="text-muted-foreground text-sm md:text-base leading-[1.7]">{cap.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Differentiation */}
      <section className="py-16 md:py-32 border-t border-border/40">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
            className="grid lg:grid-cols-12 gap-12 lg:gap-20"
          >
            <motion.div variants={fadeIn} className="lg:col-span-5">
              <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-medium block mb-4 md:mb-6">What Makes Us Different</span>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
                Not another AI writing tool
              </h2>
            </motion.div>

            <motion.div variants={fadeIn} className="lg:col-span-7 flex flex-col justify-center">
              <div className="space-y-5 md:space-y-8">
                {differentiators.map((text, i) => (
                  <motion.p
                    key={i}
                    variants={fadeIn}
                    className="text-lg md:text-2xl font-medium tracking-tight text-foreground/80 leading-[1.4] pl-5 md:pl-6 border-l-2 border-primary/30"
                  >
                    {text}
                  </motion.p>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Security */}
      <section className="py-16 md:py-32 border-t border-border/40">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            <motion.div variants={fadeIn} className="max-w-2xl mb-10 md:mb-16">
              <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-medium block mb-4 md:mb-6">Trust</span>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
                Your data stays yours.
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {securityPoints.map((point, i) => (
                <motion.div
                  key={i}
                  variants={fadeIn}
                  className="flex items-start gap-4 p-6 rounded-2xl landing-card"
                >
                  <div className="w-9 h-9 rounded-lg border border-primary/30 bg-primary/10 flex items-center justify-center shrink-0">
                    <point.icon className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-foreground/80 text-base font-medium leading-snug">{point.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-32 border-t border-border/40">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
            className="space-y-10"
          >
            <motion.h2
              variants={fadeIn}
              className="text-[30px] md:text-5xl lg:text-6xl font-medium tracking-tight text-shimmer"
            >
              Ready to win more tenders?
            </motion.h2>

            <motion.p variants={fadeIn} className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto leading-[1.75]">
              See how Sorvaine helps teams move from reactive bidding to repeatable, strategic wins.
            </motion.p>

            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-6 max-w-[320px] sm:max-w-none mx-auto">
              <CtaLink to="/signup" cta="get_started" location="platform_bottom" className="w-full sm:w-auto">
                <Button className="relative w-full sm:w-auto rounded-xl sm:rounded-md bg-primary hover:bg-primary/90 hover:-translate-y-[1px] hover:scale-[1.03] h-12 sm:h-auto px-6 sm:px-12 sm:py-3 text-[15px] sm:text-base !text-white font-semibold tracking-[0.01em] transition-all duration-200 shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.55)] sm:shadow-[0_0_20px_hsl(var(--primary)/0.15)] hover:shadow-[0_0_32px_hsl(var(--primary)/0.28)]">
                  Get started
                  <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 sm:static absolute right-5" />
                </Button>
              </CtaLink>
              <CtaLink to="/book-demo" cta="book_demo" location="platform_bottom" className="w-full sm:w-auto">
                <Button
                  variant="outline"
                  className="relative w-full sm:w-auto rounded-xl sm:rounded-md border-white/15 sm:border-white/20 bg-white/[0.03] sm:bg-transparent hover:bg-white/5 hover:border-white/30 h-12 sm:h-auto px-6 sm:px-12 sm:py-3 text-[15px] sm:text-base text-white font-semibold tracking-[0.01em] transition-all duration-200"
                >
                  Book a demo
                  <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 sm:static absolute right-5" />
                </Button>
              </CtaLink>
            </motion.div>

            <motion.p variants={fadeIn} className="text-sm text-white/70">
              No commitment. Just a 20-minute walkthrough.
            </motion.p>
          </motion.div>
        </div>
      </section>
    </PublicPageLayout>
  );
}
